import { useCallback, useEffect, useState } from "react";

// Same key the boot script in public/index.html reads before first paint.
const STORAGE_KEY = "gs-theme";

const darkQuery = () =>
  typeof window !== "undefined" && window.matchMedia
    ? window.matchMedia("(prefers-color-scheme: dark)")
    : null;

function readSaved() {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === "light" || value === "dark" ? value : null;
  } catch {
    return null;
  }
}

/**
 * Light/dark mode that follows the OS until the user picks one.
 * Toggling back to the theme the OS already shows clears the saved
 * choice, so the portal goes back to following the OS.
 */
export function useThemeMode() {
  const [saved, setSaved] = useState(readSaved);
  const [systemDark, setSystemDark] = useState(() => !!darkQuery()?.matches);

  useEffect(() => {
    const query = darkQuery();
    if (!query) return undefined;
    const onChange = (event) => setSystemDark(event.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    if (saved) root.setAttribute("data-theme", saved);
    else root.removeAttribute("data-theme");
  }, [saved]);

  const systemTheme = systemDark ? "dark" : "light";
  const theme = saved || systemTheme;

  const toggleTheme = useCallback(() => {
    const next = theme === "dark" ? "light" : "dark";
    const nextSaved = next === systemTheme ? null : next;
    try {
      if (nextSaved) localStorage.setItem(STORAGE_KEY, nextSaved);
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Storage blocked: the choice still applies for this visit.
    }
    setSaved(nextSaved);
  }, [theme, systemTheme]);

  return { theme, isDark: theme === "dark", followsSystem: !saved, toggleTheme };
}
